import { useAllLabels } from '@/hooks/useLabels';
import type { Label } from '@/types/label';

interface LabelChipsProps {
  labelIds: string[];
  /** Cap how many chips render before showing "+N". */
  max?: number;
}

/**
 * Read-only colored pill strip rendered on an inbox row. Resolves
 * `labelIds` against the shared `/me/labels` cache so the inbox response
 * itself only needs to carry ids (no `?expand=labels` round-trip).
 *
 * Unknown ids — race during initial load, or labels from a space the
 * user just left — are silently dropped. The strip hides when nothing
 * resolves.
 */
export function LabelChips({ labelIds, max = 3 }: LabelChipsProps) {
  const { byId } = useAllLabels();
  const labels = labelIds
    .map((id) => byId.get(id))
    .filter((l): l is Label => !!l);

  if (labels.length === 0) return null;
  const visible = labels.slice(0, max);
  const overflow = labels.length - visible.length;
  return (
    <span className="flex shrink-0 flex-wrap items-center justify-end gap-1">
      {visible.map((label) => (
        <LabelChip key={label.labelId} label={label} />
      ))}
      {overflow > 0 && (
        <span
          className="text-xs text-muted-foreground"
          title={labels
            .slice(max)
            .map((l) => l.name)
            .join(', ')}
        >
          +{overflow}
        </span>
      )}
    </span>
  );
}

function LabelChip({ label }: { label: Label }) {
  const color = label.color || 'var(--muted-foreground)';
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full border border-border bg-card/60 px-1.5 py-1 text-[10px] font-medium leading-none text-muted-foreground"
      title={label.description || label.name}
    >
      <span
        className="h-1.5 w-1.5 shrink-0 rounded-full"
        style={{ backgroundColor: color }}
        aria-hidden
      />
      {label.name}
    </span>
  );
}
